import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { debounceTime, distinctUntilChanged } from 'rxjs'

import {
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiOperatorSettings,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import { normalizeSearchText } from '../../../util/search-text'
import { KeyActionSelectorComponent } from '../key-action-selector/key-action-selector.component'
import {
  KeyActionViewModel,
  OPERATOR_SCORE_COUNT,
  buildKeyActionViewModels,
  selectedIdsFromKeyActions,
  withSelectedKeyActionIds,
} from '../key-action-selector/key-action-selector.logic'
import {
  KeyActionSettingsRowViewModel,
  KeyActionSettingsSort,
  buildKeyActionSettingsRows,
  filterKeyActionSettingsRows,
  formatKeyActionOperatorCount,
  sortKeyActionSettingsRows,
} from './kpi-key-action-settings.logic'

@UntilDestroy()
@Component({
  selector: 'app-kpi-key-action-settings',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    KeyActionSelectorComponent,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './kpi-key-action-settings.component.html',
  styleUrl: './kpi-key-action-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiKeyActionSettingsComponent implements OnInit {
  @Input({ required: true }) dashboard!: KpiDashboardDefinition

  settings: KpiOperatorSettings | null = null
  rows: KeyActionSettingsRowViewModel[] = []
  sort: KeyActionSettingsSort = 'default'
  readonly searchControl = new FormControl('', { nonNullable: true })
  appliedSearchQuery = ''
  countText = ''
  editingUserId: string | null = null
  editorKeyActions: KeyActionViewModel[] = []
  savingUserId: string | null = null
  isLoading = true
  loadErrorMessage = ''
  saveErrorMessage = ''
  readonly scoreCountLabel = `直近${OPERATOR_SCORE_COUNT}回平均`
  readonly scoreCountDescription = `${this.scoreCountLabel}スコアを参考に、最大3つまで選択してください。`

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((query) => {
        this.appliedSearchQuery = query
        this.recalculateRows()
        this.changeDetectorRef.markForCheck()
      })
    this.load()
  }

  /** 正規化後の検索語がある場合のみ検索中として扱う。 */
  get isSearching(): boolean {
    return normalizeSearchText(this.appliedSearchQuery) !== ''
  }

  retry(): void {
    this.load()
  }

  onSortChanged(): void {
    this.closeEditor()
    this.recalculateRows()
  }

  startEditing(viewModel: KeyActionSettingsRowViewModel): void {
    this.editingUserId = viewModel.row.userId
    this.saveErrorMessage = ''
    this.editorKeyActions = buildKeyActionViewModels(
      this.dashboard.keyActions,
      viewModel.row.keyActionScores,
      viewModel.row.selectedKeyActionIds,
    )
  }

  cancelEditing(): void {
    this.closeEditor()
  }

  onEditorSelectionChanged(selectedIds: string[]): void {
    if (this.savingUserId != null) {
      return
    }

    this.editorKeyActions = withSelectedKeyActionIds(
      this.editorKeyActions,
      selectedIds,
    )
    this.saveErrorMessage = ''
  }

  save(viewModel: KeyActionSettingsRowViewModel): void {
    if (
      this.editingUserId !== viewModel.row.userId ||
      this.savingUserId != null
    ) {
      return
    }

    const selectedIds = selectedIdsFromKeyActions(this.editorKeyActions)
    this.savingUserId = viewModel.row.userId
    this.saveErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .saveOperatorKeyActions(
        this.dashboard.id,
        viewModel.row.userId,
        selectedIds,
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (saved) => {
          if (!saved) {
            this.handleSaveError(
              new Error('saveKpiOperatorKeyActions returned false'),
            )
            return
          }

          this.updateSavedRow(viewModel.row.userId, selectedIds)
          this.savingUserId = null
          this.closeEditor()
          this.snackBar.open('重点強化KeyActionを保存しました。', '閉じる', {
            duration: 3000,
          })
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => this.handleSaveError(error),
      })
  }

  private load(): void {
    this.isLoading = true
    this.loadErrorMessage = ''
    this.saveErrorMessage = ''
    this.kpiDashboardService
      .operatorSettings(this.dashboard.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (settings) => {
          this.settings = settings
          this.isLoading = false
          this.recalculateRows()
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          captureException(error)
          this.isLoading = false
          this.loadErrorMessage =
            '重点強化KeyActionの設定を読み込めませんでした。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  private updateSavedRow(userId: string, selectedIds: string[]): void {
    if (this.settings == null) {
      throw new Error('KPI operator settings are missing')
    }

    this.settings = {
      ...this.settings,
      rows: this.settings.rows.map((row) =>
        row.userId === userId
          ? { ...row, selectedKeyActionIds: [...selectedIds] }
          : row,
      ),
    }
    this.recalculateRows()
  }

  private recalculateRows(): void {
    if (this.settings == null) {
      return
    }

    this.rows = sortKeyActionSettingsRows(
      buildKeyActionSettingsRows(this.dashboard, {
        ...this.settings,
        rows: filterKeyActionSettingsRows(
          this.settings.rows,
          this.appliedSearchQuery,
        ),
      }),
      this.sort,
    )
    this.countText = formatKeyActionOperatorCount(
      this.rows.length,
      this.settings.rows.length,
      this.appliedSearchQuery,
    )
  }

  private handleSaveError(error: unknown): void {
    captureException(error)
    this.savingUserId = null
    this.saveErrorMessage =
      '重点強化KeyActionを保存できませんでした。時間を置いて再度お試しください。'
    this.snackBar.open(this.saveErrorMessage, '閉じる')
    this.changeDetectorRef.markForCheck()
  }

  private closeEditor(): void {
    this.editingUserId = null
    this.editorKeyActions = []
    this.saveErrorMessage = ''
  }
}
